<template>
  <div class="page-shell groups-page">
    <section class="container">
      <div class="page-intro groups-page__intro">
        <div>
          <p class="eyebrow">Study Groups</p>
          <h1 class="page-title">学习小组</h1>
          <p class="page-description">
            在课程小组里交流学习进度，也可以把课内资料直接发给组内成员。
          </p>
        </div>
        <div class="groups-page__actions">
          <button class="btn btn--secondary" type="button" :disabled="loadingGroups" @click="loadGroups">
            刷新
          </button>
          <button class="btn btn--primary" type="button" @click="openCreate">
            创建小组
          </button>
        </div>
      </div>

      <div v-if="shareResourceId" class="share-banner">
        <span class="share-banner__icon" aria-hidden="true">↗</span>
        <div>
          <strong>正在转发课内资源</strong>
          <p>{{ shareTitle || '已选择一份课程资源' }}，请选择目标小组。</p>
        </div>
        <button class="share-banner__clear" type="button" @click="clearShareResource">取消</button>
      </div>

      <div v-if="groupsError" class="status-card is-error">{{ groupsError }}</div>
      <div v-else-if="loadingGroups && !groups.length" class="status-card">正在加载学习小组...</div>
      <div v-else class="groups-layout">
        <aside class="groups-sidebar panel">
          <div class="groups-sidebar__header">
            <div>
              <h2>我的小组</h2>
              <span>{{ groups.length }} 个小组</span>
            </div>
            <span class="groups-sidebar__mark" aria-hidden="true">●</span>
          </div>

          <div v-if="!groups.length" class="groups-empty">
            <span class="groups-empty__icon" aria-hidden="true">◎</span>
            <strong>还没有加入小组</strong>
            <p>加入课程后会自动进入课程学习小组，也可以创建一个新的小组。</p>
            <button class="btn btn--secondary btn--sm" type="button" @click="openCreate">创建小组</button>
          </div>

          <button
            v-for="group in groups"
            :key="group.id"
            type="button"
            :class="['group-list-item', { 'is-active': activeGroup && activeGroup.id === group.id }]"
            @click="selectGroup(group)"
          >
            <span class="group-list-item__avatar">{{ groupInitial(group) }}</span>
            <span class="group-list-item__body">
              <strong>{{ group.name || '未命名小组' }}</strong>
              <span>{{ group.member_count || 1 }} 人 · {{ group.group_type_word || (group.is_owner ? '群主' : '成员') }}</span>
              <small>{{ group.description || '暂无群公告' }}</small>
            </span>
            <span class="group-list-item__arrow" aria-hidden="true">›</span>
          </button>
        </aside>

        <section v-if="activeGroup" class="group-room panel">
          <header class="group-room__header">
            <div class="group-room__identity">
              <span class="group-room__avatar">{{ groupInitial(activeGroup) }}</span>
              <div>
                <h2>{{ activeGroup.name || '学习小组' }}</h2>
                <p>{{ activeGroup.member_count || 1 }} 位成员 · {{ activeGroup.description || '一起学习、一起进步' }}</p>
              </div>
            </div>
            <button class="btn btn--ghost btn--sm" type="button" @click="refreshActiveGroup">刷新</button>
          </header>

          <div class="room-tabs" role="tablist" aria-label="小组内容">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="['room-tab', { 'is-active': activeTab === tab.id }]"
              role="tab"
              :aria-selected="activeTab === tab.id ? 'true' : 'false'"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
              <span v-if="tab.id === 'messages' && messageTotal" class="room-tab__count">{{ messageTotal }}</span>
              <span v-if="tab.id === 'members' && members.length" class="room-tab__count">{{ members.length }}</span>
            </button>
          </div>

          <div v-if="activeTab === 'messages'" class="room-content room-content--messages">
            <div v-if="messagesError" class="status-card is-error">{{ messagesError }}</div>
            <div v-else-if="loadingMessages && !messages.length" class="status-card">正在加载群消息...</div>
            <div v-else-if="!messages.length" class="room-empty">
              <span aria-hidden="true">✦</span>
              <strong>还没有群消息</strong>
              <p>发送第一条消息，开始和小组成员交流吧。</p>
            </div>
            <div v-else class="message-list">
              <button
                v-if="hasOlderMessages"
                class="load-more"
                type="button"
                :disabled="loadingMessages"
                @click="loadOlderMessages"
              >
                {{ loadingMessages ? '加载中...' : '加载更早消息' }}
              </button>
              <article v-for="message in messages" :key="message.id" class="message-item">
                <span class="message-item__avatar">{{ groupInitial({ name: message.member_name }) }}</span>
                <div class="message-item__body">
                  <div class="message-item__meta">
                    <strong>{{ message.member_name || '群成员' }}</strong>
                    <time>{{ formatTime(message.created_at) }}</time>
                  </div>
                  <button
                    v-if="Number(message.msg_type) === 2 && message.resource_file_url"
                    class="message-item__resource is-copyable"
                    type="button"
                    :aria-label="`复制${message.resource_title || '课程资料'}链接`"
                    @click="copyResourceUrl(message)"
                  >
                    <span class="message-item__resource-icon" aria-hidden="true">▣</span>
                    <div>
                      <strong>{{ message.resource_title || '课程资料' }}</strong>
                      <small>课内资源转发</small>
                    </div>
                  </button>
                  <div v-else-if="Number(message.msg_type) === 2" class="message-item__resource">
                    <span class="message-item__resource-icon" aria-hidden="true">▣</span>
                    <div>
                      <strong>{{ message.resource_title || '课程资料' }}</strong>
                      <small>课内资源转发</small>
                    </div>
                  </div>
                  <p>{{ message.content || (Number(message.msg_type) === 2 ? '分享了一份资料' : '') }}</p>
                </div>
              </article>
            </div>

            <form class="message-composer" @submit.prevent="sendMessage">
              <input
                v-model.trim="messageText"
                type="text"
                maxlength="1000"
                placeholder="输入群消息..."
                :disabled="sending"
              >
              <button class="btn btn--primary" type="submit" :disabled="sending || !messageText">
                {{ sending ? '发送中...' : '发送' }}
              </button>
            </form>
          </div>

          <div v-else class="room-content room-content--members">
            <div v-if="membersError" class="status-card is-error">{{ membersError }}</div>
            <div v-else-if="loadingMembers && !members.length" class="status-card">正在加载成员...</div>
            <div v-else-if="!members.length" class="room-empty"><strong>暂无成员信息</strong></div>
            <div v-else class="member-grid">
              <article v-for="member in members" :key="member.id" class="member-item">
                <span class="member-item__avatar">{{ groupInitial({ name: member.member_name }) }}</span>
                <div>
                  <strong>{{ member.member_name || '未命名学员' }}</strong>
                  <span>{{ Number(member.role) === 1 ? (member.role_word || '群主') : '成员' }}</span>
                </div>
              </article>
            </div>
          </div>

          <footer v-if="shareResourceId" class="room-share-footer">
            <div>
              <strong>转发给当前小组</strong>
              <span>{{ shareTitle || '课程资源' }}</span>
            </div>
            <button class="btn btn--primary" type="button" :disabled="sharing" @click="forwardToActiveGroup">
              {{ sharing ? '转发中...' : '转发资料' }}
            </button>
          </footer>
        </section>

        <section v-else class="group-room panel group-room--empty">
          <span class="group-room--empty__icon" aria-hidden="true">◎</span>
          <h2>选择一个小组</h2>
          <p>从左侧选择小组，查看群消息和成员。</p>
        </section>
      </div>
    </section>

    <div v-if="createVisible" class="modal-backdrop" @click.self="closeCreate">
      <section class="create-dialog panel" role="dialog" aria-modal="true" aria-labelledby="create-group-title">
        <div class="create-dialog__header">
          <div>
            <p class="eyebrow">New Group</p>
            <h2 id="create-group-title">创建学习小组</h2>
          </div>
          <button class="icon-button" type="button" aria-label="关闭" @click="closeCreate">×</button>
        </div>
        <form @submit.prevent="submitCreate">
          <label class="form-field">
            <span>小组名称</span>
            <input v-model.trim="groupForm.name" maxlength="80" placeholder="例如：Redis 进阶讨论组" required>
          </label>
          <label class="form-field">
            <span>小组简介</span>
            <textarea v-model.trim="groupForm.description" maxlength="500" rows="3" placeholder="告诉成员这个小组主要交流什么"></textarea>
          </label>
          <label class="form-field">
            <span>初始成员 ID <small>可选，多个用逗号分隔</small></span>
            <input v-model.trim="memberIdsText" placeholder="例如：1002,1003">
          </label>
          <div v-if="createError" class="form-error">{{ createError }}</div>
          <div class="create-dialog__actions">
            <button class="btn btn--secondary" type="button" @click="closeCreate">取消</button>
            <button class="btn btn--primary" type="submit" :disabled="creating">
              {{ creating ? '创建中...' : '创建小组' }}
            </button>
          </div>
        </form>
      </section>
    </div>

    <transition name="copy-toast">
      <div v-if="copyNotice" class="resource-copy-toast" role="status">
        {{ copyNotice }}
      </div>
    </transition>
  </div>
</template>

<script>
import {
  createMemberGroup,
  fetchMemberGroupMembers,
  fetchMemberGroupMessages,
  fetchMyMemberGroups,
  sendMemberGroupMessage
} from '@/api/memberGroup'
import { forwardMaterial } from '@/api/courseResource'

const emptyGroupForm = () => ({ name: '', description: '' })

export default {
  name: 'StudyGroupsView',
  data() {
    return {
      groups: [],
      activeGroup: null,
      groupsError: '',
      loadingGroups: false,
      activeTab: 'messages',
      tabs: [
        { id: 'messages', label: '消息' },
        { id: 'members', label: '成员' }
      ],
      messages: [],
      messageTotal: 0,
      messagePage: 1,
      messagePageSize: 20,
      messagesError: '',
      loadingMessages: false,
      members: [],
      membersError: '',
      loadingMembers: false,
      messageText: '',
      sending: false,
      shareResourceId: 0,
      shareTitle: '',
      sharing: false,
      createVisible: false,
      creating: false,
      createError: '',
      groupForm: emptyGroupForm(),
      memberIdsText: '',
      copyNotice: '',
      copyNoticeTimer: null
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.messageTotal / this.messagePageSize))
    },
    hasOlderMessages() {
      return this.messagePage < this.totalPages
    }
  },
  created() {
    this.readShareQuery()
    this.loadGroups()
  },
  beforeDestroy() {
    if (this.copyNoticeTimer) window.clearTimeout(this.copyNoticeTimer)
  },
  methods: {
    readShareQuery() {
      const query = this.$route.query || {}
      this.shareResourceId = Number(query.shareResourceId || query.resource_id) || 0
      this.shareTitle = this.decodeQuery(query.shareTitle || query.resource_title || '')
    },
    decodeQuery(value) {
      try {
        return decodeURIComponent(String(value || ''))
      } catch (error) {
        return String(value || '')
      }
    },
    async loadGroups() {
      this.loadingGroups = true
      this.groupsError = ''
      try {
        this.groups = await fetchMyMemberGroups()
        if (this.activeGroup) {
          const current = this.groups.find(item => Number(item.id) === Number(this.activeGroup.id))
          this.activeGroup = current || null
        }
        if (!this.activeGroup && this.groups.length) this.selectGroup(this.groups[0])
      } catch (error) {
        this.groupsError = error.message || '学习小组加载失败'
      } finally {
        this.loadingGroups = false
      }
    },
    selectGroup(group) {
      if (!group || (this.activeGroup && Number(this.activeGroup.id) === Number(group.id) && this.messages.length)) return
      this.activeGroup = group
      this.activeTab = 'messages'
      this.refreshActiveGroup()
    },
    async refreshActiveGroup() {
      if (!this.activeGroup) return
      await Promise.all([this.refreshMessages(), this.loadMembers()])
    },
    async refreshMessages() {
      this.messagePage = 1
      this.messageTotal = 0
      this.messages = []
      await this.loadMessages()
    },
    async loadMessages() {
      if (!this.activeGroup || this.loadingMessages) return
      this.loadingMessages = true
      this.messagesError = ''
      try {
        const result = await fetchMemberGroupMessages(this.activeGroup.id, {
          page: this.messagePage,
          page_size: this.messagePageSize
        })
        const ordered = [...result.list].reverse()
        this.messages = this.messagePage === 1 ? ordered : ordered.concat(this.messages)
        this.messageTotal = Number(result.total) || 0
        this.messagePage = Number(result.page) || this.messagePage
        this.messagePageSize = Number(result.page_size) || this.messagePageSize
      } catch (error) {
        this.messagesError = error.message || '群消息加载失败'
      } finally {
        this.loadingMessages = false
      }
    },
    async loadOlderMessages() {
      if (!this.hasOlderMessages || this.loadingMessages) return
      this.messagePage += 1
      await this.loadMessages()
    },
    async loadMembers() {
      if (!this.activeGroup) return
      this.loadingMembers = true
      this.membersError = ''
      try {
        this.members = await fetchMemberGroupMembers(this.activeGroup.id)
      } catch (error) {
        this.membersError = error.message || '小组成员加载失败'
      } finally {
        this.loadingMembers = false
      }
    },
    async sendMessage() {
      const content = String(this.messageText || '').trim()
      if (!this.activeGroup || !content || this.sending) return
      this.sending = true
      try {
        await sendMemberGroupMessage(this.activeGroup.id, content)
        this.messageText = ''
        await this.refreshMessages()
      } catch (error) {
        this.messagesError = error.message || '消息发送失败'
      } finally {
        this.sending = false
      }
    },
    async forwardToActiveGroup() {
      if (!this.activeGroup || !this.shareResourceId || this.sharing) return
      this.sharing = true
      try {
        await forwardMaterial({
          resource_id: this.shareResourceId,
          channel: 5,
          group_id: this.activeGroup.id,
          remark: this.shareTitle ? `推荐资料：${this.shareTitle}` : '推荐这份资料'
        })
        this.messagesError = ''
        await this.refreshMessages()
        this.clearShareResource()
      } catch (error) {
        this.messagesError = error.message || '资料转发失败'
      } finally {
        this.sharing = false
      }
    },
    clearShareResource() {
      this.shareResourceId = 0
      this.shareTitle = ''
      if (this.$route.query.shareResourceId || this.$route.query.resource_id) {
        const query = { ...this.$route.query }
        delete query.shareResourceId
        delete query.resource_id
        delete query.shareTitle
        delete query.resource_title
        this.$router.replace({ name: 'study-groups', query }).catch(() => {})
      }
    },
    openCreate() {
      this.groupForm = emptyGroupForm()
      this.memberIdsText = ''
      this.createError = ''
      this.createVisible = true
    },
    closeCreate() {
      this.createVisible = false
      this.creating = false
      this.groupForm = emptyGroupForm()
      this.memberIdsText = ''
      this.createError = ''
    },
    async submitCreate() {
      if (!this.groupForm.name || this.creating) return
      this.creating = true
      this.createError = ''
      try {
        const memberIds = this.memberIdsText
          .split(/[，,]/)
          .map(value => Number(String(value).trim()) || 0)
          .filter(Boolean)
        await createMemberGroup({ ...this.groupForm, member_ids: memberIds })
        this.closeCreate()
        await this.loadGroups()
      } catch (error) {
        this.createError = error.message || '小组创建失败'
      } finally {
        this.creating = false
      }
    },
    groupInitial(group = {}) {
      return String(group.name || '组').trim().slice(0, 1).toUpperCase()
    },
    formatTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async copyResourceUrl(message = {}) {
      const url = String(message.resource_file_url || '').trim()
      if (!url) return

      try {
        if (window.isSecureContext && window.navigator.clipboard) {
          await window.navigator.clipboard.writeText(url)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = url
          textarea.setAttribute('readonly', '')
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          try {
            textarea.select()
            if (!document.execCommand('copy')) throw new Error('copy failed')
          } finally {
            document.body.removeChild(textarea)
          }
        }
        this.showCopyNotice('复制成功')
      } catch (error) {
        this.showCopyNotice('复制失败，请稍后重试')
      }
    },
    showCopyNotice(message) {
      if (this.copyNoticeTimer) window.clearTimeout(this.copyNoticeTimer)
      this.copyNotice = message
      this.copyNoticeTimer = window.setTimeout(() => {
        this.copyNotice = ''
        this.copyNoticeTimer = null
      }, 1800)
    }
  }
}
</script>

<style scoped lang="scss">
@import '../styles/tokens';

.groups-page {
  padding-bottom: 64px;
}

.groups-page__intro {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.groups-page__actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.share-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 20px;
  padding: 14px 18px;
  border: 1px solid rgba($color-primary, 0.18);
  border-radius: $radius-md;
  background: $color-primary-light;
  color: $color-text-primary;
}

.share-banner__icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $color-primary;
  color: #fff;
  font-size: 20px;
}

.share-banner strong,
.share-banner p {
  display: block;
  margin: 0;
}

.share-banner p {
  margin-top: 3px;
  color: $color-text-secondary;
  font-size: 13px;
}

.share-banner__clear {
  margin-left: auto;
  padding: 4px 8px;
  border: 0;
  background: transparent;
  color: $color-text-secondary;
  cursor: pointer;
}

.groups-layout {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 20px;
  min-height: 620px;
}

.groups-sidebar,
.group-room {
  min-width: 0;
  overflow: hidden;
  background: $color-surface;
}

.groups-sidebar {
  padding: 0;
}

.groups-sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid $color-border-light;
}

.groups-sidebar__header h2 {
  margin: 0;
  font-size: 18px;
}

.groups-sidebar__header span {
  display: block;
  margin-top: 4px;
  color: $color-text-tertiary;
  font-size: 12px;
}

.groups-sidebar__mark {
  margin: 0 !important;
  color: $color-primary !important;
  font-size: 22px !important;
}

.group-list-item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  padding: 15px 18px;
  border: 0;
  border-bottom: 1px solid $color-border-light;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
}

.group-list-item:hover,
.group-list-item.is-active {
  background: #fff8f5;
}

.group-list-item.is-active {
  box-shadow: inset 3px 0 0 $color-primary;
}

.group-list-item__avatar,
.group-room__avatar,
.message-item__avatar,
.member-item__avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: $gradient-primary;
  color: #fff;
  font-weight: 700;
  line-height: 1;
}

.group-list-item__avatar {
  width: 42px;
  height: 42px;
}

.group-list-item__body {
  display: block;
  min-width: 0;
  flex: 1;
}

.group-list-item__body strong,
.group-list-item__body span,
.group-list-item__body small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-list-item__body strong {
  color: $color-text-primary;
  font-size: 14px;
}

.group-list-item__body span {
  margin-top: 4px;
  color: $color-primary-dark;
  font-size: 12px;
}

.group-list-item__body small {
  margin-top: 4px;
  color: $color-text-tertiary;
  font-size: 12px;
}

.group-list-item__arrow {
  color: $color-text-tertiary;
  font-size: 22px;
}

.groups-empty,
.room-empty {
  padding: 58px 24px;
  color: $color-text-secondary;
  text-align: center;
}

.groups-empty__icon,
.group-room--empty__icon,
.room-empty > span {
  display: block;
  margin-bottom: 14px;
  color: rgba($color-primary, 0.55);
  font-size: 40px;
}

.groups-empty strong,
.room-empty strong {
  display: block;
  color: $color-text-primary;
}

.groups-empty p,
.room-empty p,
.group-room--empty p {
  margin: 8px auto 18px;
  max-width: 280px;
  line-height: 1.6;
  font-size: 13px;
}

.group-room {
  display: flex;
  flex-direction: column;
  padding: 0;
}

.group-room__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid $color-border-light;
}

.group-room__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.group-room__avatar {
  width: 46px;
  height: 46px;
}

.group-room__identity h2,
.group-room__identity p {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-room__identity h2 {
  color: $color-text-primary;
  font-size: 18px;
}

.group-room__identity p {
  margin-top: 4px;
  color: $color-text-secondary;
  font-size: 12px;
}

.room-tabs {
  display: flex;
  gap: 22px;
  padding: 0 22px;
  border-bottom: 1px solid $color-border-light;
}

.room-tab {
  position: relative;
  padding: 13px 2px 11px;
  border: 0;
  background: transparent;
  color: $color-text-secondary;
  cursor: pointer;
}

.room-tab.is-active {
  color: $color-primary-dark;
  font-weight: 700;
}

.room-tab.is-active::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: $color-primary;
  content: '';
}

.room-tab__count {
  display: inline-block;
  min-width: 18px;
  margin-left: 5px;
  padding: 1px 4px;
  border-radius: 10px;
  background: $color-primary-light;
  color: $color-primary-dark;
  font-size: 11px;
  text-align: center;
}

.room-content {
  min-height: 0;
  flex: 1;
}

.room-content--messages {
  display: flex;
  flex-direction: column;
}

.message-list {
  min-height: 360px;
  flex: 1;
  padding: 18px 22px;
  overflow-y: auto;
}

.load-more {
  display: block;
  margin: 0 auto 16px;
  border: 0;
  background: transparent;
  color: $color-primary-dark;
  cursor: pointer;
  font-size: 12px;
}

.message-item {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
}

.message-item__avatar,
.member-item__avatar {
  width: 34px;
  height: 34px;
  background: #fff0ea;
  color: $color-primary-dark;
  font-size: 13px;
}

.message-item__body {
  min-width: 0;
  max-width: 78%;
}

.message-item__meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.message-item__meta strong {
  color: $color-text-primary;
  font-size: 13px;
}

.message-item__meta time {
  color: $color-text-tertiary;
  font-size: 11px;
}

.message-item__body p {
  margin: 5px 0 0;
  color: $color-text-secondary;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-item__resource {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 9px 11px;
  border: 1px solid $color-border-light;
  border-radius: $radius-sm;
  background: #fafafa;
  font: inherit;
  text-align: left;
}

.message-item__resource.is-copyable {
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
}

.message-item__resource.is-copyable:hover,
.message-item__resource.is-copyable:focus-visible {
  border-color: rgba($color-primary, 0.45);
  background: $color-primary-light;
  outline: none;
}

.message-item__resource-icon {
  color: $color-primary;
  font-size: 18px;
}

.message-item__resource strong,
.message-item__resource small {
  display: block;
}

.message-item__resource strong {
  color: $color-text-primary;
  font-size: 12px;
}

.message-item__resource small {
  margin-top: 2px;
  color: $color-text-tertiary;
  font-size: 11px;
}

.resource-copy-toast {
  position: fixed;
  bottom: 32px;
  left: 50%;
  z-index: 90;
  padding: 10px 18px;
  border-radius: $radius-sm;
  background: rgba(17, 24, 39, 0.92);
  color: #fff;
  font-size: 13px;
  transform: translateX(-50%);
  box-shadow: $shadow-lg;
}

.copy-toast-enter-active,
.copy-toast-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.copy-toast-enter,
.copy-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}

.message-composer {
  display: flex;
  gap: 10px;
  padding: 14px 22px;
  border-top: 1px solid $color-border-light;
}

.message-composer input,
.form-field input,
.form-field textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid $color-border;
  border-radius: $radius-sm;
  background: #fff;
  color: $color-text-primary;
  font: inherit;
}

.message-composer input {
  min-width: 0;
  padding: 10px 12px;
}

.message-composer input:focus,
.form-field input:focus,
.form-field textarea:focus {
  border-color: $color-primary;
  outline: 2px solid rgba($color-primary, 0.12);
}

.member-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  padding: 22px;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px;
  border: 1px solid $color-border-light;
  border-radius: $radius-sm;
}

.member-item > div strong,
.member-item > div span {
  display: block;
}

.member-item strong {
  color: $color-text-primary;
  font-size: 13px;
}

.member-item div span {
  margin-top: 3px;
  color: $color-text-tertiary;
  font-size: 11px;
}

.room-share-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 22px;
  border-top: 1px solid rgba($color-primary, 0.15);
  background: $color-primary-light;
}

.room-share-footer strong,
.room-share-footer span {
  display: block;
}

.room-share-footer strong {
  color: $color-text-primary;
  font-size: 13px;
}

.room-share-footer span {
  margin-top: 3px;
  max-width: 420px;
  overflow: hidden;
  color: $color-text-secondary;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.group-room--empty {
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
}

.group-room--empty h2,
.group-room--empty p {
  margin: 0;
}

.group-room--empty h2 {
  font-size: 19px;
}

.group-room--empty p {
  margin-top: 8px;
  color: $color-text-secondary;
  font-size: 13px;
}

.modal-backdrop {
  position: fixed;
  z-index: 300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(26, 26, 26, 0.45);
}

.create-dialog {
  width: min(100%, 480px);
  padding: 24px;
}

.create-dialog__header,
.create-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.create-dialog__header {
  margin-bottom: 22px;
}

.create-dialog__header h2 {
  margin: 4px 0 0;
  font-size: 20px;
}

.icon-button {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: $color-text-secondary;
  cursor: pointer;
  font-size: 24px;
}

.form-field {
  display: block;
  margin-bottom: 16px;
}

.form-field > span {
  display: block;
  margin-bottom: 7px;
  color: $color-text-primary;
  font-size: 13px;
  font-weight: 700;
}

.form-field > span small {
  color: $color-text-tertiary;
  font-weight: 400;
}

.form-field input,
.form-field textarea {
  padding: 10px 12px;
}

.form-field textarea {
  resize: vertical;
}

.form-error {
  margin-bottom: 14px;
  color: $color-danger;
  font-size: 13px;
}

.create-dialog__actions {
  justify-content: flex-end;
  margin-top: 22px;
}

@media (max-width: 820px) {
  .groups-page__intro {
    align-items: flex-start;
    flex-direction: column;
  }

  .groups-layout {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .groups-sidebar {
    max-height: 360px;
    overflow-y: auto;
  }

  .group-room {
    min-height: 560px;
  }
}

@media (max-width: 520px) {
  .groups-page__actions,
  .groups-page__actions .btn {
    width: 100%;
  }

  .groups-page__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .group-room__header,
  .message-composer,
  .room-share-footer {
    padding-right: 14px;
    padding-left: 14px;
  }

  .message-list,
  .member-grid {
    padding-right: 14px;
    padding-left: 14px;
  }

  .message-item__body {
    max-width: calc(100% - 44px);
  }

  .room-share-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .room-share-footer .btn {
    width: 100%;
  }
}
</style>
